import React, { useEffect, useState, useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { BarChart, PieChart, LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AdminLayout from './AdminLayout';
import supabase from '../../supabase/supabase';

echarts.use([BarChart, PieChart, LineChart, GridComponent, TooltipComponent, LegendComponent, TitleComponent, CanvasRenderer]);

const { FiDownload, FiCalendar } = FiIcons;

const FILTER_OPTIONS = [
  { label: 'Today', value: 'today' },
  { label: 'Last 3 Days', value: '3days' },
  { label: 'Last 7 Days', value: '7days' },
  { label: 'Last 30 Days', value: '30days' },
  { label: 'Last 6 Months', value: '6months' },
  { label: 'Last 1 Year', value: '1year' },
  { label: 'All Time', value: 'all' },
  { label: 'Custom Range', value: 'custom' }
];

function getDateRange(filter, customStart, customEnd) {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  let start;
  switch (filter) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case '3days':
      start = new Date(now); start.setDate(start.getDate() - 2); start.setHours(0, 0, 0, 0);
      break;
    case '7days':
      start = new Date(now); start.setDate(start.getDate() - 6); start.setHours(0, 0, 0, 0);
      break;
    case '30days':
      start = new Date(now); start.setDate(start.getDate() - 29); start.setHours(0, 0, 0, 0);
      break;
    case '6months':
      start = new Date(now); start.setMonth(start.getMonth() - 6); start.setDate(1); start.setHours(0, 0, 0, 0);
      break;
    case '1year':
      start = new Date(now); start.setFullYear(start.getFullYear() - 1); start.setDate(1); start.setHours(0, 0, 0, 0);
      break;
    case 'custom':
      start = customStart ? new Date(customStart + 'T00:00:00') : new Date(2020, 0, 1);
      const customEndDate = customEnd ? new Date(customEnd + 'T23:59:59') : end;
      return { start, end: customEndDate };
    default:
      start = new Date(2020, 0, 1);
  }
  return { start, end };
}

function AdminAnalytics() {
  const [events, setEvents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('30days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const today = new Date();

  useEffect(() => {
    const load = async () => {
      const [{ data: eventData }, { data: orderData }] = await Promise.all([
        supabase
          .from('analytics_events_1750000001')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('orders_1750000001')
          .select('*')
          .order('created_at', { ascending: false })
      ]);
      setEvents(eventData || []);
      setOrders(orderData || []);
      setLoading(false);
    };
    load();
  }, []);

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => getDateRange(filter, customStart, customEnd),
    [filter, customStart, customEnd]
  );

  const filteredEvents = useMemo(() => events.filter((e) => {
    if (!e.created_at) return false;
    const d = new Date(e.created_at);
    return d >= rangeStart && d <= rangeEnd;
  }), [events, rangeStart, rangeEnd]);

  const filteredOrders = useMemo(() => orders.filter((o) => {
    if (!o.created_at) return false;
    const d = new Date(o.created_at);
    return d >= rangeStart && d <= rangeEnd;
  }), [orders, rangeStart, rangeEnd]);

  const eventTypeCounts = filteredEvents.reduce((acc, e) => {
    acc[e.event_type] = (acc[e.event_type] || 0) + 1;
    return acc;
  }, {});

  const refundedOrders = filteredOrders.filter((order) => order.status === 'refunded');
  const cancelledOrders = filteredOrders.filter((order) => order.status === 'cancelled');
  const refundAmount = refundedOrders.reduce((sum, order) => sum + Number(order.refund_amount || order.total_amount || 0), 0);

  const pieData = Object.entries(eventTypeCounts).map(([name, value]) => ({ name, value }));

  const dayCount = Math.max(1, Math.ceil((rangeEnd - rangeStart) / (1000 * 60 * 60 * 24)));
  const chartDays = Math.min(dayCount, 30);

  const last7Days = Array.from({ length: chartDays }, (_, i) => {
    const d = new Date(rangeEnd);
    d.setDate(d.getDate() - (chartDays - 1 - i));
    return d.toISOString().slice(0, 10);
  });

  const dailyCounts = last7Days.map((day) => ({
    day,
    count: filteredEvents.filter((e) => e.created_at && e.created_at.slice(0, 10) === day).length
  }));

  const pieOption = {
    tooltip: { trigger: 'item' },
    color: ['#0e8f5b', '#3b82f6', '#8b5cf6', '#d97706', '#e11d48'],
    series: [{
      type: 'pie',
      radius: ['40%', '70%'],
      label: { show: true, fontSize: 12 },
      data: pieData.length ? pieData : [{ name: 'No data', value: 0 }]
    }]
  };

  const lineOption = {
    tooltip: { trigger: 'axis' },
    grid: { left: 40, right: 20, top: 20, bottom: 30 },
    xAxis: { type: 'category', data: dailyCounts.map((d) => d.day.slice(5)), axisLabel: { fontSize: 11, rotate: chartDays > 14 ? 45 : 0 } },
    yAxis: { type: 'value', minInterval: 1 },
    series: [{
      type: 'line',
      data: dailyCounts.map((d) => d.count),
      smooth: true,
      lineStyle: { color: '#0e8f5b', width: 3 },
      areaStyle: { color: 'rgba(14,143,91,0.08)' },
      itemStyle: { color: '#0e8f5b' }
    }]
  };

  const barOption = {
    tooltip: { trigger: 'axis' },
    grid: { left: 40, right: 20, top: 20, bottom: 30 },
    xAxis: { type: 'category', data: Object.keys(eventTypeCounts), axisLabel: { fontSize: 11 } },
    yAxis: { type: 'value', minInterval: 1 },
    series: [{
      type: 'bar',
      data: Object.values(eventTypeCounts),
      itemStyle: { color: '#0e8f5b', borderRadius: [6, 6, 0, 0] },
      barWidth: '50%'
    }]
  };

  const downloadReport = () => {
    const reportRows = [
      ['Order', 'Date', 'Customer', 'Status', 'Total Amount', 'Refund Amount', 'Promo Code', 'Delivery Mode'],
      ...filteredOrders.map((order) => [
        order.id,
        new Date(order.created_at).toLocaleDateString(),
        order.customer_name,
        order.status,
        order.total_amount,
        order.refund_amount,
        order.promo_code,
        order.delivery_mode
      ])
    ];
    const csv = reportRows
      .map((row) => row.map((value) => `"${String(value || '').replaceAll('"', '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `store-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div><h1>Shop Analytics</h1><p>Track visitor activity and engagement</p><p>Updated <time dateTime={today.toISOString().slice(0, 10)}>{today.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</time></p></div>
        <button className="admin-btn primary" onClick={downloadReport}>
          <SafeIcon icon={FiDownload} /> Download Orders Report
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '0 28px', marginBottom: '20px' }}>
        <SafeIcon icon={FiCalendar} style={{ color: '#6e716e' }} />
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '0.78rem',
              fontWeight: filter === opt.value ? 600 : 500,
              border: filter === opt.value ? '1.5px solid #0e8f5b' : '1.5px solid #e5e7eb',
              background: filter === opt.value ? '#f0fdf4' : 'white',
              color: filter === opt.value ? '#0e8f5b' : '#555',
              cursor: 'pointer',
              transition: 'all 200ms',
              whiteSpace: 'nowrap'
            }}
          >
            {opt.label}
          </button>
        ))}
        {filter === 'custom' && (
          <>
            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: '0.78rem' }} />
            <span style={{ color: '#6e716e', fontSize: '0.78rem' }}>to</span>
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: '0.78rem' }} />
          </>
        )}
      </div>
      {loading ? (
        <div className="admin-loading">Loading analytics…</div>
      ) : (
        <>
          <div className="admin-stats">
            <div className="admin-stat-card">
              <div className="stat-icon" style={{ color: '#0e8f5b', background: '#eaf7f1' }}>
                <span style={{ fontSize: '1.3rem' }}>📊</span>
              </div>
              <div className="stat-info">
                <div className="stat-value">{filteredEvents.length}</div>
                <div className="stat-label">Total Events</div>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon" style={{ color: '#3b82f6', background: '#eff6ff' }}>
                <span style={{ fontSize: '1.3rem' }}>👁</span>
              </div>
              <div className="stat-info">
                <div className="stat-value">{eventTypeCounts.page_view || 0}</div>
                <div className="stat-label">Page Views</div>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon" style={{ color: '#8b5cf6', background: '#f5f3ff' }}>
                <span style={{ fontSize: '1.3rem' }}>🛒</span>
              </div>
              <div className="stat-info">
                <div className="stat-value">{eventTypeCounts.add_to_cart || 0}</div>
                <div className="stat-label">Add to Cart</div>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon" style={{ color: '#d97706', background: '#fffbeb' }}>
                <span style={{ fontSize: '1.3rem' }}>💰</span>
              </div>
              <div className="stat-info">
                <div className="stat-value">{eventTypeCounts.purchase || 0}</div>
                <div className="stat-label">Purchases</div>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon" style={{ color: '#dc2626', background: '#fef2f2' }}>
                <span style={{ fontSize: '1.3rem' }}>↩</span>
              </div>
              <div className="stat-info">
                <div className="stat-value">{refundedOrders.length}</div>
                <div className="stat-label">Total Refunds</div>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon" style={{ color: '#dc2626', background: '#fff1f2' }}>
                <span style={{ fontSize: '1.3rem' }}>₨</span>
              </div>
              <div className="stat-info">
                <div className="stat-value">Rs. {refundAmount.toLocaleString()}</div>
                <div className="stat-label">Refund Amount</div>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon" style={{ color: '#92400e', background: '#fffbeb' }}>
                <span style={{ fontSize: '1.3rem' }}>×</span>
              </div>
              <div className="stat-info">
                <div className="stat-value">{cancelledOrders.length}</div>
                <div className="stat-label">Cancellations</div>
              </div>
            </div>
          </div>
          <div className="admin-charts-grid">
            <div className="admin-card" style={{ margin: 0 }}>
              <h2>Events Over 7 Days</h2>
              <ReactEChartsCore echarts={echarts} option={lineOption} style={{ height: 280 }} />
            </div>
            <div className="admin-card" style={{ margin: 0 }}>
              <h2>Event Types</h2>
              <ReactEChartsCore echarts={echarts} option={pieOption} style={{ height: 280 }} />
            </div>
          </div>
          <div className="admin-card">
            <h2>Events by Category</h2>
            <ReactEChartsCore echarts={echarts} option={barOption} style={{ height: 300 }} />
          </div>
        </>
      )}
    </AdminLayout>
  );
}

export default AdminAnalytics;